export type ProxyProfileImportItem = {
  profile_key: string;
  display_name?: string;
  protocol: string;
  server: string;
  server_port: number;
  username?: string;
  password?: string;
  probe_exit_ip?: string;
  tags?: string;
  state?: string;
};

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function proxyProfileKeyFromParts(
  index: number,
  protocol: string,
  server: string,
  port: number,
): string {
  const safeServer =
    server
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "proxy";
  return `${protocol}-${safeServer}-${port}-${String(index).padStart(2, "0")}`;
}

export function proxyImportItemFromLine(
  rawLine: string,
  index: number,
): ProxyProfileImportItem {
  const line = rawLine.trim();
  if (!line) {
    throw new Error("empty proxy line");
  }
  if (!line.includes("://") && line.includes(":")) {
    const parts = line.split(":");
    if (parts.length === 2 || parts.length === 4) {
      const server = parts[0].trim();
      const port = Number(parts[1].trim());
      if (!server) {
        throw new Error("proxy server required");
      }
      if (!Number.isInteger(port) || port <= 0 || port > 65535) {
        throw new Error(`invalid proxy port: ${parts[1]}`);
      }
      const protocol = "socks";
      return {
        profile_key: proxyProfileKeyFromParts(index, protocol, server, port),
        protocol,
        server,
        server_port: port,
        username: parts.length === 4 ? parts[2].trim() : "",
        password: parts.length === 4 ? parts[3].trim() : "",
        state: "enabled",
      };
    }
  }
  let url: URL;
  try {
    url = new URL(line);
  } catch (err) {
    throw new Error(`invalid proxy URI: ${errorMessage(err)}`, {
      cause: err,
    });
  }
  const scheme = url.protocol.replace(":", "").toLowerCase();
  if (scheme !== "socks" && scheme !== "socks5" && scheme !== "http") {
    throw new Error(`unsupported proxy URI scheme: ${scheme || "<empty>"}`);
  }
  const port = Number(url.port);
  if (!url.hostname || !Number.isInteger(port) || port <= 0 || port > 65535) {
    throw new Error("proxy URI requires host and port");
  }
  const protocol = scheme === "socks5" ? "socks" : scheme;
  const profileKey =
    url.searchParams.get("profile_key")?.trim() ||
    url.searchParams.get("key")?.trim() ||
    proxyProfileKeyFromParts(index, protocol, url.hostname, port);
  return {
    profile_key: profileKey,
    display_name:
      url.searchParams.get("display_name")?.trim() ||
      url.searchParams.get("name")?.trim() ||
      "",
    protocol,
    server: url.hostname,
    server_port: port,
    username: decodeURIComponent(url.username || ""),
    password: decodeURIComponent(url.password || ""),
    probe_exit_ip:
      url.searchParams.get("probe_exit_ip")?.trim() ||
      url.searchParams.get("exit_ip")?.trim() ||
      "",
    tags: url.searchParams.get("tags")?.trim() || "",
    state: url.searchParams.get("state")?.trim() || "enabled",
  };
}

export function parseProxyProfilesImportBody(raw: string): {
  items: ProxyProfileImportItem[];
} {
  const text = raw.trim();
  if (!text) {
    throw new Error("导入内容不能为空");
  }
  try {
    const parsed = JSON.parse(text) as unknown;
    if (Array.isArray(parsed)) {
      return { items: parsed as ProxyProfileImportItem[] };
    }
    if (parsed && typeof parsed === "object") {
      const record = parsed as Record<string, unknown>;
      const items = record.items ?? record.profiles;
      if (Array.isArray(items)) {
        return { items: items as ProxyProfileImportItem[] };
      }
    }
    throw new Error("JSON 必须是数组或包含 items/profiles 数组");
  } catch (err) {
    if (!(err instanceof SyntaxError)) {
      throw err;
    }
  }
  const items: ProxyProfileImportItem[] = [];
  text.split(/\r?\n/).forEach((line, lineIndex) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("#")) {
      return;
    }
    try {
      items.push(proxyImportItemFromLine(trimmed, items.length + 1));
    } catch (err) {
      throw new Error(`第 ${lineIndex + 1} 行无效：${errorMessage(err)}`, {
        cause: err,
      });
    }
  });
  if (items.length === 0) {
    throw new Error("没有可导入的代理行");
  }
  return { items };
}
